import React from "react";
import type { DiagramCell, DiagramEdge, DiagramModel, Point, TextBlock } from "@/lib/drawio/model";

/**
 * DiagramSvg — renders a compiled draw.io model as inline SVG.
 *
 * Pure presentation, no hooks: usable from server components and from the
 * client island alike. Every label is a real `<text>` node (crawlable,
 * selectable, read by screen readers); every cell is a `<g data-cell>` the
 * island can address. State (active, focus, reach) arrives as props and
 * only changes class names, so hydration output equals server output.
 */

export interface DiagramSvgState {
  active: string | null;
  /** Cells kept bright; when set, everything else dims. */
  focus: ReadonlySet<string> | null;
  /** Cells reached from the active one along edges (highlighted in navy). */
  reach: ReadonlySet<string> | null;
}

export interface DiagramSvgProps {
  model: DiagramModel;
  idPrefix: string;
  title: string;
  description: string;
  /** Cell id → accessible name for cells that carry config details. */
  interactive: ReadonlyMap<string, string>;
  /** Cell id → plain-text explanation, rendered as <desc> inside the cell. */
  descriptions?: ReadonlyMap<string, string>;
  state: DiagramSvgState;
}

const ARROW_LENGTH = 12;

function cornerPath(points: Point[], radius: number): string {
  if (points.length === 0) return "";
  let d = `M${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length - 1; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    const c = points[i + 1];
    const ab = Math.hypot(b.x - a.x, b.y - a.y);
    const bc = Math.hypot(c.x - b.x, c.y - b.y);
    const r = Math.min(radius, ab / 2, bc / 2);
    if (r <= 0.01) {
      d += ` L${b.x} ${b.y}`;
      continue;
    }
    const p1 = { x: b.x + ((a.x - b.x) / ab) * r, y: b.y + ((a.y - b.y) / ab) * r };
    const p2 = { x: b.x + ((c.x - b.x) / bc) * r, y: b.y + ((c.y - b.y) / bc) * r };
    d += ` L${round(p1.x)} ${round(p1.y)} Q${b.x} ${b.y} ${round(p2.x)} ${round(p2.y)}`;
  }
  const last = points[points.length - 1];
  d += ` L${last.x} ${last.y}`;
  return d;
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}

function fontClass(family: string): string {
  return `dg-f-${family.toLowerCase()}`;
}

function Label({ block }: { block: TextBlock }) {
  return (
    <text
      className={`dg-text ${fontClass(block.family)}`}
      fontSize={block.size}
      fill={block.color}
      fontWeight={block.bold ? 700 : 400}
      fontStyle={block.italic ? "italic" : undefined}
      textAnchor={block.anchor}
      xmlSpace="preserve"
      style={{ whiteSpace: "pre" }}
    >
      {block.lines.map((line, lineIndex) => (
        <tspan key={lineIndex} x={block.x} y={round(block.y + lineIndex * block.lineHeight)} dominantBaseline="central">
          {line.runs.map((run, runIndex) => {
            const plain =
              run.bold === undefined && run.italic === undefined && run.underline === undefined && run.family === undefined && run.color === undefined && run.size === undefined;
            if (plain) return <React.Fragment key={runIndex}>{run.text}</React.Fragment>;
            return (
              <tspan
                key={runIndex}
                className={run.family ? fontClass(run.family) : undefined}
                fontWeight={run.bold === undefined ? undefined : run.bold ? 700 : 400}
                fontStyle={run.italic ? "italic" : undefined}
                textDecoration={run.underline ? "underline" : undefined}
                fill={run.color}
                fontSize={run.size}
              >
                {run.text}
              </tspan>
            );
          })}
        </tspan>
      ))}
    </text>
  );
}

function Shape({ cell }: { cell: DiagramCell }) {
  const fill = cell.fill ?? "none";
  const stroke = cell.stroke ?? "none";
  const dash = cell.dashed ? `${cell.strokeWidth * 3} ${cell.strokeWidth * 3}` : undefined;
  const common = { className: "dg-shape", fill, stroke, strokeWidth: cell.stroke ? cell.strokeWidth : 0, strokeDasharray: dash };
  if (cell.shape === "image") {
    return <image href={cell.image ?? undefined} x={cell.x} y={cell.y} width={cell.w} height={cell.h} preserveAspectRatio="xMidYMid meet" />;
  }
  if (cell.shape === "text") return null;
  if (cell.shape === "ellipse") {
    return <ellipse {...common} cx={cell.x + cell.w / 2} cy={cell.y + cell.h / 2} rx={cell.w / 2} ry={cell.h / 2} />;
  }
  if (cell.shape === "step") {
    const s = cell.stepSize;
    const pts = [
      [cell.x, cell.y],
      [cell.x + cell.w - s, cell.y],
      [cell.x + cell.w, cell.y + cell.h / 2],
      [cell.x + cell.w - s, cell.y + cell.h],
      [cell.x, cell.y + cell.h],
      [cell.x + s, cell.y + cell.h / 2],
    ];
    return <polygon {...common} points={pts.map((p) => p.join(",")).join(" ")} />;
  }
  return <rect {...common} x={cell.x} y={cell.y} width={cell.w} height={cell.h} rx={cell.radius} ry={cell.radius} />;
}

function isInvisible(cell: DiagramCell): boolean {
  return cell.shape !== "image" && cell.fill === null && cell.stroke === null && cell.text === null;
}

function edgeBright(edge: DiagramEdge, bright: (id: string) => boolean): boolean {
  const a = edge.source ? bright(edge.source) : true;
  const b = edge.target ? bright(edge.target) : true;
  return a && b;
}

export default function DiagramSvg({ model, idPrefix, title, description, interactive, descriptions, state }: DiagramSvgProps) {
  const dimming = state.focus !== null || state.reach !== null;
  const bright = (id: string) =>
    !dimming || id === state.active || (state.focus !== null && state.focus.has(id)) || (state.reach !== null && state.reach.has(id));

  const markerColors = Array.from(new Set(model.edges.filter((e) => e.endArrow !== "none" || e.startArrow !== "none").map((e) => e.stroke)));
  const titleId = `${idPrefix}-title`;
  const descId = `${idPrefix}-desc`;

  return (
    <svg
      className="dg-svg"
      viewBox={`0 0 ${model.width} ${model.height}`}
      width={model.width}
      height={model.height}
      role="group"
      aria-labelledby={`${titleId} ${descId}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title id={titleId}>{title}</title>
      <desc id={descId}>{description}</desc>
      <defs>
        {markerColors.map((color) => (
          <marker
            key={color}
            id={`${idPrefix}-arrow-${color.replace("#", "")}`}
            viewBox={`0 0 ${ARROW_LENGTH} ${ARROW_LENGTH}`}
            refX={ARROW_LENGTH - 2.5}
            refY={ARROW_LENGTH / 2}
            markerWidth={ARROW_LENGTH}
            markerHeight={ARROW_LENGTH}
            markerUnits="userSpaceOnUse"
            orient="auto-start-reverse"
          >
            <path d={`M0 1.5 L${ARROW_LENGTH} ${ARROW_LENGTH / 2} L0 ${ARROW_LENGTH - 1.5} Z`} fill={color} />
          </marker>
        ))}
      </defs>
      <rect x={0} y={0} width={model.width} height={model.height} fill={model.background} />
      {model.cells.map((cell) => {
        if (cell.id === "bg" || isInvisible(cell)) return null;
        const name = interactive.get(cell.id);
        const isInteractive = name !== undefined;
        const desc = descriptions?.get(cell.id);
        const descId = desc ? `${idPrefix}-${cell.id}-desc` : undefined;
        const classes = ["dg-cell", `dg-cell--${cell.shape}`];
        if (isInteractive) classes.push("is-interactive");
        if (state.active === cell.id) classes.push("is-active");
        if (state.reach !== null && state.reach.has(cell.id) && state.active !== cell.id) classes.push("is-reach");
        if (!bright(cell.id)) classes.push("is-dim");
        return (
          <g
            key={cell.id}
            data-cell={cell.id}
            className={classes.join(" ")}
            tabIndex={isInteractive ? 0 : undefined}
            role={isInteractive ? "button" : undefined}
            aria-label={isInteractive ? name : undefined}
            aria-pressed={isInteractive ? state.active === cell.id : undefined}
            aria-describedby={descId}
          >
            {desc ? <desc id={descId}>{desc}</desc> : null}
            <Shape cell={cell} />
            {cell.text ? <Label block={cell.text} /> : null}
          </g>
        );
      })}
      {model.edges.map((edge) => {
        const classes = ["dg-edge"];
        if (!edgeBright(edge, bright)) classes.push("is-dim");
        else if (dimming && state.reach !== null) classes.push("is-reach");
        const marker = (head: DiagramEdge["endArrow"]) => (head === "none" ? undefined : `url(#${idPrefix}-arrow-${edge.stroke.replace("#", "")})`);
        return (
          <g key={edge.id} data-edge={edge.id} data-source={edge.source ?? undefined} data-target={edge.target ?? undefined} className={classes.join(" ")}>
            <path
              d={cornerPath(edge.points, 8)}
              fill="none"
              stroke={edge.stroke}
              strokeWidth={edge.strokeWidth}
              strokeDasharray={edge.dashed ? `${edge.strokeWidth * 3} ${edge.strokeWidth * 3}` : undefined}
              strokeLinecap="butt"
              strokeLinejoin="round"
              markerEnd={marker(edge.endArrow)}
              markerStart={marker(edge.startArrow)}
            />
            {edge.label ? <Label block={edge.label} /> : null}
          </g>
        );
      })}
    </svg>
  );
}
